import { debounceTime, takeUntil } from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  Provider,
  SimpleChanges,
  OnDestroy,
  ElementRef,
  Self,
  Optional,
  DoCheck,
  HostBinding,
  Injector,
  ChangeDetectorRef,
  TemplateRef
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { MatFormFieldControl } from '@angular/material/form-field';
import { FocusMonitor } from '@angular/cdk/a11y';
const SEARCH_SELECT_FORM_FIELD_CONTROL: Provider = {
  provide: MatFormFieldControl,
  useExisting: forwardRef(() => SelectSearchComponent),
};

@Component({
  selector: 'app-select-search',
  templateUrl: './select-search.component.html',
  styleUrls: ['./select-search.component.scss'],
  providers: [
    SEARCH_SELECT_FORM_FIELD_CONTROL
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectSearchComponent
  implements OnInit, OnChanges, DoCheck, OnDestroy, ControlValueAccessor, MatFormFieldControl<SelectSearchComponent> {
  private static nextId = 0;
  public options = new BehaviorSubject<any[]>([]);
  public valueControl = new FormControl();
  public filterControl = new FormControl();
  private destroy$ = new Subject();
  @Input() isLoading = false;
  // mat-form-field fields
  stateChanges = new Subject<void>();
  focused: boolean;
  touched = false;
  empty: boolean;
  required: boolean;
  errorState = false;
  controlType?: string;
  autofilled?: boolean;
  userAriaDescribedBy?: string;
  @Input() optionTemplate: TemplateRef<any>;
  // mat-form-field fields

  /** value of the dropdown */
  @Input()
  get value() {
    return this.valueControl.value;
  }
  set value(val) {
    this.valueControl.setValue(val);
    //this.onChange();
  }
  @Input() public noEntriesFoundLabel: string = 'Варианты не найдены'
  @Input() public placeholderLabel: string = 'Поиск'
  /**
   * Get the required inputs
   */
  @Input() public data: any[] = [];
  @Input() public multiple = false;

  /**
   * Get the placeholder
   */
  @Input() public placeholder: any;

  /**
   * Get the placeholder
   */
  @Input() public valueKey: any = 'id';

  /**
   * Get the placeholder
   */
  @Input() public displayKey: any = 'name';

  /**
   * configuration options
   */
  @Input() public config: any = {};

  /**
   * Value
   */
  @Input() public disabled: boolean;

  /**
   * change event when value changes to provide user to handle things in change event
   */
  @Output() public change: EventEmitter<any> = new EventEmitter();

  /**
   * The search text change event emitter emitted when user type in the search input
   */
  @Output() public searchChange: EventEmitter<any> = new EventEmitter();

  /**
   * Event emitted when dropdown is open.
   */
  @Output() public open: EventEmitter<any> = new EventEmitter();

  /**
   * Event emitted when dropdown is open.
   */
  @Output() public close: EventEmitter<any> = new EventEmitter();

  @HostBinding() id = `app-select-search-${SelectSearchComponent.nextId++}`;

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  /**
   * Component onInit
   */
  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private focusMonitor: FocusMonitor,
    @Optional() @Self() public ngControl: NgControl
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    this.setupEventListeners();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data) {
      const data: any[] = changes.data.currentValue;
      this.options.next(data);
      //this.cdr.detectChanges();
    }
    this.stateChanges.next();
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched;
      this.stateChanges.next();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef);
  }


  public onChange: any = (v) => {
    // empty
  }
  public onTouched: any = () => {
    // empty
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.valueControl.disable();
    } else {
      this.valueControl.enable();
    }
  }

  setDescribedByIds(ids: string[]): void {
    const controlElement = this.elementRef.nativeElement;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick(event: MouseEvent): void {
  }

  onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (!this.elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }


  public reset() {
  }
  private setupEventListeners() {
    this.valueControl.valueChanges
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        this.onChange(value);
        if (this.data) {
          if (this.multiple) {
            const item = this.data.filter(a => value.includes(a.id));
            this.change.emit(item);
          }
          else {
            const item = this.data.find(a => a.id == value);
            this.change.emit(item);
          }
        }
      });

    this.filterControl.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(1000))
      .subscribe((s) => {
        this.searchChange.emit(s)
        this.handleFilter();
      });
  }

  private handleFilter() {
    if (!this.data) {
      return;
    }
    // get the search keyword
    let search = this.filterControl.value;
    if (!search) {
      this.options.next(this.data.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.options.next(
      this.data.filter(item => item[this.displayKey]?.toLowerCase().indexOf(search) > -1)
    );
  }
  getDisplayText(item: any) {
    if (item) {
      let resultText = '';
      if (Array.isArray(this.displayKey)) {

        this.displayKey.forEach((v) => {
          resultText += ' ' + item[v];
        })

      }
      else {
        resultText = item[this.displayKey]
      }
      return resultText ? resultText.trim() : `${this.displayKey} not found`;
    }
  }
  getTriggerText(value) {
    if (this.data && value != null) {
      if (this.multiple) {
        const item = this.data.filter(a => value.includes(a.id));
        return item?.map(t => this.getDisplayText(t)).join(',');
      }
      else {
        const item = this.data.find(a => a.id == value);
        return this.getDisplayText(item);
      }
    }
  }

}
